/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Link from '@components/Link';
import { PageComponent } from '@root/pages/Type';
import { ServiceData, ServiceState, singleLoad } from '@root/store/services';
import { Dropdown } from 'react-bootstrap';
import { DropdownMenuProps } from 'react-bootstrap/esm/DropdownMenu';
import classNames from 'classnames';
import styles from './Item.module.scss';
import CaretUp from '@static/img/icons/caret-up.svg';
import CaretDown from '@static/img/icons/caret-down.svg';
import PlayIcon from '@static/img/icons/play.svg';
import RestartIcon from '@static/img/icons/restart.svg';
import StopIcon from '@static/img/icons/stop.svg';
import RestartAltIcon from '@static/img/icons/restart-alt.svg';
import { useAxios } from '@root/utils/axios';
import { ActionReponse, actionType } from '../Detail/MassActions';
import Noty from '@root/utils/Noty';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@root/store/types';

type ItemControlProps = {
    service: ServiceData;
};

type ItemControlMenuToggleProps = DropdownMenuProps & {
    className?: string;
    onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

const ItemControlMenuToggle = React.forwardRef<
    HTMLButtonElement,
    ItemControlMenuToggleProps
>(({ className, onClick }, ref) => {
    return (
        <button
            className={classNames(
                'btn btn-outline-primary dropdown-toggle',
                className,
            )}
            type="button"
            ref={ref}
            onClick={(e) => {
                e.preventDefault();
                if (onClick) {
                    onClick(e);
                }
            }}
        >
            <CaretUp className={styles.caret_up} />
            <CaretDown className={styles.caret_down} />
        </button>
    );
});

ItemControlMenuToggle.displayName = 'ItemControlMenuToggle';

type ItemControlMenuProps = DropdownMenuProps & {};

const ItemControlMenu = React.forwardRef<HTMLDivElement, ItemControlMenuProps>(
    ({ children, className }, ref) => {
        return (
            <div
                ref={ref}
                className={classNames(className, styles.dropdown_menu)}
            >
                <ul
                    className={classNames(
                        'list-unstyled',
                        styles.dropdown_List,
                    )}
                >
                    {children}
                </ul>
            </div>
        );
    },
);

ItemControlMenu.displayName = 'ItemControlMenu';

const ItemControl: PageComponent<ItemControlProps> = ({ service }) => {
    const [show, setShow] = useState(false);

    const { t } = useTranslation('uiServicesMain');
    const axiosInstance = useAxios();
    const dispatch = useDispatch<AppDispatch>();

    const handleAction = (action: Omit<actionType, 'console'>) => {
        axiosInstance
            .post<ActionReponse>(
                `/account/services/${service.orderNo}/${action}`,
            )
            .then((response) => {
                if (response.data.invalidAction) {
                    Noty({
                        title: t('control.noty.error.title', {
                            defaultValue: 'Klaida',
                        }),
                        message: t('control.noty.error.message', {
                            defaultValue: 'Klaidingas veiksmas.',
                        }),
                        type: 'danger',
                    });
                    return;
                }
                if (!response.data.success) {
                    Noty({
                        title: t('control.noty.fatalError.title', {
                            defaultValue: 'Klaida',
                        }),
                        message: t('control.noty.fatalError.message', {
                            defaultValue:
                                'Nepavyko atlikti veiksmo, prašome pamėginti dar kartą.',
                        }),
                        type: 'danger',
                    });
                    return;
                }
                Noty({
                    title: t('control.noty.title', {
                        defaultValue: 'Inicijuota',
                    }),
                    message: t('control.noty.message', {
                        defaultValue: 'Veiksmas inicijuotas sėkmingai.',
                    }),
                    type: 'success',
                });
                dispatch(singleLoad(service.orderNo));
            });
    };

    return (
        <div className={classNames(styles.control_group)}>
            <div
                className={classNames(styles.control, {
                    [styles.control_margin]:
                        service.type !== 'vps' || service.isCarantine === true,
                })}
            >
                <Link
                    href="ui_services_detail"
                    params={{ id: service.orderNo }}
                >
                    <a
                        className={classNames(
                            'btn btn-primary btn-block',
                            styles.controlBtn,
                        )}
                    >
                        <Trans i18nKey="button.details" ns="uiServicesMain">
                            Valdyti
                        </Trans>
                    </a>
                </Link>
                {service.type === 'vps' && service.isCarantine === false && (
                    <Dropdown
                        className={classNames(styles.dropdown, {
                            [styles.show]: show,
                        })}
                        onToggle={(isOpen) => {
                            setShow(isOpen);
                        }}
                        onSelect={(eventKey: any, _event: any) =>
                            handleAction(eventKey)
                        }
                    >
                        <Dropdown.Toggle
                            as={ItemControlMenuToggle}
                            id="dropdown-custom-components"
                            className={styles.dropdown_toggle}
                        />

                        <Dropdown.Menu align="right" as={ItemControlMenu}>
                            <Dropdown.Item
                                eventKey="start"
                                className={classNames(styles.dropdown_item, {
                                    [styles.disabled]:
                                        service.state !== ServiceState.STOPPED,
                                })}
                                disabled={
                                    service.state !== ServiceState.STOPPED
                                }
                            >
                                <PlayIcon />
                                <Trans
                                    i18nKey="control.buttons.start"
                                    ns="uiServicesMain"
                                >
                                    Paleisti
                                </Trans>
                            </Dropdown.Item>
                            <Dropdown.Item
                                eventKey="restart"
                                className={classNames(styles.dropdown_item, {
                                    [styles.disabled]:
                                        service.state !== ServiceState.RUNING &&
                                        service.state !== ServiceState.STOPPED,
                                })}
                                disabled={
                                    service.state !== ServiceState.RUNING &&
                                    service.state !== ServiceState.STOPPED
                                }
                            >
                                <RestartIcon />
                                <Trans
                                    i18nKey="control.buttons.restart"
                                    ns="uiServicesMain"
                                >
                                    Perkrauti
                                </Trans>
                            </Dropdown.Item>
                            <Dropdown.Item
                                eventKey="stop"
                                className={classNames(styles.dropdown_item, {
                                    [styles.disabled]:
                                        service.state !== ServiceState.RUNING,
                                })}
                                disabled={service.state !== ServiceState.RUNING}
                            >
                                <StopIcon />
                                <Trans
                                    i18nKey="control.buttons.stop"
                                    ns="uiServicesMain"
                                >
                                    Stabdyti
                                </Trans>
                            </Dropdown.Item>
                            <Dropdown.Item
                                eventKey="change_os"
                                as={Link}
                                href="ui_services_os"
                                params={{ id: service.orderNo }}
                            >
                                <a
                                    className={classNames(
                                        styles.dropdown_item,
                                        'dropdown-item',
                                    )}
                                >
                                    <RestartAltIcon />
                                    <Trans
                                        i18nKey="control.buttons.osChange"
                                        ns="uiServicesMain"
                                    >
                                        Perrašyti OS
                                    </Trans>
                                </a>
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </div>
            <div className={styles.controlRenewBtn}>
                <Link href="ui_services_renew" params={{ id: service.orderNo }}>
                    <a
                        className={classNames(
                            'btn btn-outline-primary btn-block',
                        )}
                    >
                        <span>
                            <Trans i18nKey="button.renew" ns="uiServicesMain">
                                Pratesti
                            </Trans>
                        </span>
                    </a>
                </Link>
            </div>
        </div>
    );
};

export default ItemControl;
